import { useState } from 'react';

import { ModalBase } from './ModalBase';

import type { AvailablePlaybook } from '../../types';
import type React from 'react';

export interface ForceTrocaPlaybookModalProps {
  open: boolean;
  /** Playbooks REAIS (available-playbooks). Vazio → degrada honesto. */
  playbooks: AvailablePlaybook[];
  isLoadingPlaybooks: boolean;
  /** true quando a busca de playbooks falhou (degrada honesto). */
  playbooksError: boolean;
  isPending: boolean;
  /** true quando o último envio falhou — `role=alert`. */
  failed: boolean;
  /** Desfecho honesto da RESPOSTA do servidor (null enquanto não houver). */
  outcome: string | null;
  onClose: () => void;
  onConfirm: (newPlaybookId: string, reason: string) => void;
}

/** Backend exige reason ≥20 — mesmo piso na UI. */
const REASON_FLOOR = 20;

/**
 * ForceTrocaPlaybookModal — converte modals-3.jsx (ForceTrocaPlaybookModal).
 * Diferente do protótipo (4 playbooks hardcoded), o select usa os playbooks
 * REAIS de `available-playbooks`; vazio/erro degrada honesto ("nenhum playbook
 * disponível") e desabilita o confirmar. reason ≥20. Desfecho da resposta.
 */
export function ForceTrocaPlaybookModal({
  open,
  playbooks,
  isLoadingPlaybooks,
  playbooksError,
  isPending,
  failed,
  outcome,
  onClose,
  onConfirm,
}: ForceTrocaPlaybookModalProps): React.JSX.Element | null {
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  if (!open) return null;

  const hasPlaybooks = playbooks.length > 0;
  // Default para o primeiro playbook real quando ainda não houve escolha.
  const effectiveSelected = selected !== '' ? selected : (playbooks[0]?.id ?? '');
  const canSubmit =
    hasPlaybooks && effectiveSelected !== '' && reason.trim().length >= REASON_FLOOR && !isPending;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Trocar playbook"
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              onConfirm(effectiveSelected, reason);
            }}
          >
            {isPending ? 'Confirmando…' : 'Confirmar'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <label className="modal-form__field">
          <span className="modal-form__label">Playbook</span>
          {isLoadingPlaybooks ? (
            <p style={{ color: 'var(--fg-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
              Carregando playbooks…
            </p>
          ) : !hasPlaybooks ? (
            <p style={{ color: 'var(--fg-3)', fontSize: 'var(--text-sm)', margin: 0 }}>
              {playbooksError
                ? 'Não foi possível carregar os playbooks disponíveis.'
                : 'Nenhum playbook disponível para esta conversa.'}
            </p>
          ) : (
            <select
              className="modal-form__select"
              value={effectiveSelected}
              onChange={(e) => {
                setSelected(e.target.value);
              }}
              aria-label="Playbook"
            >
              {playbooks.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · v{p.version}
                </option>
              ))}
            </select>
          )}
        </label>
        <label className="modal-form__field">
          <span className="modal-form__label">
            Motivo (obrigatório · mínimo {REASON_FLOOR} caracteres)
          </span>
          <textarea
            className="modal-form__textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={3}
            placeholder="A IA está conduzindo no playbook errado para o estágio atual da conversa…"
            aria-label="Motivo da troca de playbook"
          />
        </label>
        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível trocar o playbook.</span>
            </div>
          </div>
        )}
        {outcome !== null && (
          <div className="modal-form__callout" role="status">
            {outcome}
          </div>
        )}
      </div>
    </ModalBase>
  );
}
